import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import FileDownloadSharpIcon from '@mui/icons-material/FileDownloadSharp';

import { COLOR_GREY_400, COLOR_GREY_600, COLOR_PRIMARY_1, COLOR_PRIMARY_2 } from 'src/colors';
import {
  COLOR_GREY,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  ProductIDOptions,
  exchangeRates,
} from 'src/const';
import { formatCurrencyBasedOnCountryCode } from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Payment } from 'src/App/HOC';

const expertFeatures = {
  expert: [
    '3 years historical data',
    'Map Dynamic Data Updating ⭐',
    'Amenities and Performance Filters ⭐',
    'Custom Market ⭐',
    'Unlimited Airbnb Revenue Reports',
    'Money back guarantee',
  ],
  scale: [
    'Historical Data through 2021',
    'Individual Listing Details',
    'Complete Host Inisghts (20 Markets)',
    'Market Overview Table (20 Markets) Download',
    'Market Details Table (20 Markets) Download',
    'Property Manager Yable (20 Markets) Download',
    'Money back guarantee',
  ],
};

const FeatureList = ({ plan }) => {
  return (
    <List disablePadding={true} dense={true}>
      {expertFeatures[plan].map((feature, index) => (
        <ListItem key={index} disableGutters={true} dense={true}>
          <ListItemIcon sx={{ minWidth: '30px', marginRight: '0px' }}>
            <DoneIcon sx={{ color: 'green' }} />
          </ListItemIcon>
          {feature.includes('Money') ? (
            <ListItemText primary={<Typography sx={{ fontWeight: 500 }}>{feature}</Typography>} />
          ) : feature.includes('Download') ? (
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  {feature.replace('Download', '')}
                  <FileDownloadSharpIcon />
                </Typography>
              }
            />
          ) : (
            <ListItemText primary={feature} />
          )}
        </ListItem>
      ))}
    </List>
  );
};

const UpgradeSubscriptionModal = ({ plan, setPlan }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const search = useSelector((state: any) => state.dashboard.search);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];
  const [duration, setDuration] = useState('yearly');
  const [proratedPrice, setProratedPrice] = useState<null | number>(null);
  const [subscriptionUpgradeLoading, setSubscriptionUpgradeLoading] = useState(false);
  const [showPaymentCard, setShowPaymentCard] = useState(false);

  const price =
    duration == 'monthly'
      ? formatCurrencyBasedOnCountryCode(
          parseFloat(subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly'].toFixed(2)),
          userLocalCurrency
        )
      : formatCurrencyBasedOnCountryCode(
          parseFloat(
            (subscriptionPriceAccordingToUserLocalCurrency[plan][duration] / 12).toFixed()
          ),
          userLocalCurrency
        );

  const handleClose = (args: any = null) => {
    setPlan(null);
  };

  const getProratedUpgradePrice = async () => {
    setProratedPrice(null);

    try {
      const url =
        'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
      const testProductID = 'price_1PQoO6BVoeq938b5NtkhWK5L';
      const config = {
        queryParams: {
          newProductID: ProductIDOptions[plan].productID[duration],
          newProductName: plan,
          newDuration: duration,
          operation: 'fetch_proration_price',
          //   isTest: true,
        },
      };
      const resp = await callApi(url, config, true);
      const prorationAmt = resp.proration_amount;
      if (prorationAmt && !isNaN(Number(prorationAmt))) {
        setProratedPrice(prorationAmt);
      } else {
        setProratedPrice(subscriptionPriceAccordingToUserLocalCurrency[plan][duration]);
      }
    } catch (err) {
      setProratedPrice(subscriptionPriceAccordingToUserLocalCurrency[plan][duration]);
    }
  };

  useEffect(() => {
    getProratedUpgradePrice();
  }, [duration]);

  const upgradeSubscription = async () => {
    try {
      setSubscriptionUpgradeLoading(true);
      const url =
        'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
      const testProductID = 'price_1PQoO6BVoeq938b5NtkhWK5L';
      const config = {
        queryParams: {
          newProductID: ProductIDOptions[plan].productID[duration],
          newProductName: plan,
          newDuration: duration,
          operation: 'modify_subscription',
          //   isTest: true,
        },
      };
      const resp = await callApi(url, config, true);
      setSubscriptionUpgradeLoading(false);
      if (resp.subscription_modification_status == 'success') {
        handleClose();
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setShowPaymentCard(true);
      }
    } catch (err) {
      setSubscriptionUpgradeLoading(false);
    }
  };

  return (
    <Modal open={plan ? true : false} onClose={handleClose} sx={{ zIndex: 2000 }}>
      <Grid
        container
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        sx={{
          background: 'white',
          width: matches ? '100vw' : '500px',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: 4,
          outline: 'none !important',
          padding: 3,
          height: matches ? '100vh' : '90vh',
          overflow: 'auto',
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h5"
              sx={{ color: COLOR_PRIMARY_2, fontWeight: 500, textTransform: 'capitalize' }}
            >
              Upgrade to {plan} Plan
            </Typography>
            <CloseIcon
              sx={{ color: COLOR_GREY_400, cursor: 'pointer', fontWeight: 'bold' }}
              onClick={handleClose}
            />
          </Grid>
          <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <FeatureList plan={plan} />
          </Grid>
          <Grid container item direction="row" justifyContent="flex-start" xs={12}>
            <Typography style={{ fontWeight: 'bold', marginTop: '20px' }}>
              Upgrade Summary
            </Typography>
          </Grid>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
          >
            <Typography variant="body2">Plan</Typography>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={duration}
                onChange={(event, value) => setDuration(value)}
              >
                <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
                <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
          >
            <Typography variant="body2">New Price</Typography>
            <Typography variant="body2">
              {exchangeRates[userLocalCurrency].symbol}
              {price}{' '}
              <span
                style={{
                  fontSize: '14px',
                  color: COLOR_GREY_600,
                  textDecoration: 'none !important',
                }}
              >
                /month
              </span>
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
            <Divider />
          </Grid>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
            sx={{ marginBottom: '10px' }}
          >
            <Typography variant="body2">
              Subtotal (per {duration == 'monthly' ? 'month' : 'year'})
            </Typography>
            <Typography variant="body2">
              {exchangeRates[userLocalCurrency].symbol}
              {formatCurrencyBasedOnCountryCode(
                parseFloat(
                  subscriptionPriceAccordingToUserLocalCurrency[plan][duration].toFixed(2)
                ),
                userLocalCurrency
              )}
            </Typography>
          </Grid>

          {!showPaymentCard ? (
            <>
              <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                xs={12}
              >
                <Typography variant="body2">Total Amount Due Today</Typography>
                {proratedPrice ? (
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLOR_PRIMARY_2 }}>
                    {exchangeRates[userLocalCurrency].symbol}
                    {formatCurrencyBasedOnCountryCode(proratedPrice, userLocalCurrency)}
                  </Typography>
                ) : (
                  <CircularProgress />
                )}
              </Grid>
              <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                xs={12}
                sx={{ marginTop: '25px' }}
              >
                <Button
                  sx={{
                    background: COLOR_PRIMARY_2,
                    color: 'white',
                    width: '100%',
                    ':hover': {
                      background: COLOR_PRIMARY_1,
                      color: 'white',
                    },
                  }}
                  onClick={upgradeSubscription}
                  disabled={subscriptionUpgradeLoading}
                >
                  Confirm Changes {subscriptionUpgradeLoading && <CircularProgress />}
                </Button>
              </Grid>
            </>
          ) : (
            <Grid
              item
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              xs={12}
              sx={{ mt: 2 }}
            >
              <Grid
                container
                item
                sx={{
                  border: `2px solid ${COLOR_GREY}`,
                  borderRadius: 10,
                  padding: theme.spacing(2, 3, 2),
                  boxShadow: theme.shadows[5],
                }}
              >
                <Payment
                  countryCode={search.data.countryCode}
                  city={search.data.city}
                  district={search.data.name}
                  product={plan}
                  duration={duration}
                  promoCode=""
                  setSubscriptionModalOpen={handleClose}
                  discountedPrice={null}
                  countryToUnlock={null}
                  subscriptionPriceAccordingToUserLocalCurrency={
                    subscriptionPriceAccordingToUserLocalCurrency
                  }
                  userLocalCurrency={userLocalCurrency}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Modal>
  );
};

export default UpgradeSubscriptionModal;
